import React, { useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { UserAvatar } from '@/components/shared';
import { Users } from 'lucide-react';

// Approx max height of the popover (8 rows × ~40px). Used to decide if there's
// enough room above the anchor to render upward without going off-screen.
const POPOVER_MAX_HEIGHT = 320;

/**
 * MentionSuggestions — filtered dropdown for @mention autocomplete.
 * Shared by comment input and post composer.
 *
 * Supports two kinds of mentionable items in a single merged list:
 *   - Users  (members[])  -> { user_id, user_name, role, avatar_url? }
 *   - Groups (groups[])   -> { id, name, tag, color, member_count }
 *
 * Selected items are passed to onSelect with a `kind` discriminator
 * ('user' | 'group') so callers can branch on type.
 *
 * Exposes handleKeyDown via ref for keyboard navigation (Arrow keys + Enter/Tab).
 */
const MentionSuggestions = forwardRef(({ query, members, groups, onSelect, currentUserId, loading = false }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [openUp, setOpenUp] = useState(true);
  // Viewport-relative coordinates for the portal-rendered popover. We render an
  // invisible anchor inline (so parent layout/measurement is unchanged) and
  // portal the actual UI to document.body using these fixed-position coords —
  // this guarantees the popover can never be clipped by parent overflow or
  // sibling stacking contexts (e.g., post cards below the composer).
  const [coords, setCoords] = useState(null);
  const anchorRef = useRef(null);

  const filtered = useMemo(() => {
    if (query === null || query === undefined) return [];
    const q = (query || '').toLowerCase();

    // --- Users
    const userResults = [];
    if (members?.length) {
      const others = members.filter(m => m.user_id !== currentUserId);
      const startsWith = [];
      const contains = [];
      for (const m of others) {
        const name = m.user_name?.toLowerCase() || '';
        if (!q) {
          startsWith.push({ kind: 'user', ...m });
        } else if (name.startsWith(q) || name.split(' ').some(part => part.startsWith(q))) {
          startsWith.push({ kind: 'user', ...m });
        } else if (name.includes(q)) {
          contains.push({ kind: 'user', ...m });
        }
      }
      userResults.push(...startsWith, ...contains);
    }

    // --- Groups (matched on tag, with name fallback)
    const groupResults = [];
    if (groups?.length) {
      const startsWith = [];
      const contains = [];
      for (const g of groups) {
        const tag = (g.tag || '').toLowerCase();
        const name = (g.name || '').toLowerCase();
        if (!q) {
          startsWith.push({ kind: 'group', ...g });
        } else if (tag.startsWith(q) || name.startsWith(q)) {
          startsWith.push({ kind: 'group', ...g });
        } else if (tag.includes(q) || name.includes(q)) {
          contains.push({ kind: 'group', ...g });
        }
      }
      groupResults.push(...startsWith, ...contains);
    }

    // Groups first (more compact, often fewer of them), then users. Cap at 8 total.
    return [...groupResults.slice(0, 4), ...userResults].slice(0, 8);
  }, [query, members, groups, currentUserId]);

  // Reset selection when results change
  useEffect(() => { setSelectedIndex(0); }, [query, filtered.length]);

  // Popover visible when we either have results to show OR are loading them
  const isVisible = filtered.length > 0 || (loading && query !== null);

  // Auto-flip direction: open upward by default; flip to downward if there
  // Compute open-direction AND viewport coords from the inline anchor's parent.
  // The anchor is an invisible <span> that lives in the normal layout flow next
  // to the editor — its parentElement is the editor wrapper we want to align to.
  const computePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const parent = anchor?.parentElement;
    if (!parent) return;
    const r = parent.getBoundingClientRect();
    const spaceAbove = r.top;
    const spaceBelow = window.innerHeight - r.bottom;
    const up = spaceAbove >= POPOVER_MAX_HEIGHT || spaceAbove >= spaceBelow;
    setOpenUp(up);
    setCoords({
      left: r.left,
      width: r.width,
      // For openUp, anchor at parent's top edge (we'll translateY(-100%) the popover via CSS)
      // For openDown, anchor at parent's bottom edge
      top: up ? r.top : r.bottom,
    });
  }, []);

  // Measure synchronously before paint so direction + position are correct on first render
  useLayoutEffect(() => {
    if (isVisible) computePosition();
  }, [isVisible, computePosition]);

  // Re-measure on viewport resize AND scroll (capture phase so we catch ancestors)
  useEffect(() => {
    if (!isVisible) return;
    const handler = () => computePosition();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [isVisible, computePosition]);

  useImperativeHandle(ref, () => ({
    handleKeyDown(e) {
      if (!filtered.length) return false;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => (i + 1) % filtered.length);
        return true;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => (i - 1 + filtered.length) % filtered.length);
        return true;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey || e.shiftKey)) return false;
        e.preventDefault();
        onSelect(filtered[selectedIndex]);
        return true;
      }
      return false;
    }
  }), [filtered, selectedIndex, onSelect]);

  if (!isVisible) return null;

  const showLoading = filtered.length === 0 && loading;

  // Portal target: document.body. SSR-safe guard.
  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  // Inline invisible anchor — used purely for measuring the parent (editor wrapper)
  const anchor = <span ref={anchorRef} aria-hidden="true" style={{ display: 'none' }} />;

  // While we don't yet have coords, render only the anchor (it'll trigger the
  // useLayoutEffect to compute coords; next render produces the popover).
  if (!coords || !portalTarget) return anchor;

  const popoverStyle = {
    position: 'fixed',
    left: `${coords.left}px`,
    width: `${coords.width}px`,
    top: openUp ? `${coords.top}px` : `${coords.top}px`,
    transform: openUp ? 'translateY(calc(-100% - 4px))' : 'translateY(4px)',
    // z-index intentionally very high to escape any future stacking contexts
    zIndex: 9999,
  };

  return (
    <>
      {anchor}
      {createPortal(
        <div
          style={popoverStyle}
          className="bg-card border border-slate-200 dark:border-white/10 rounded-lg shadow-lg overflow-x-hidden overflow-y-auto max-h-80"
          data-testid="mention-suggestions"
        >
          {showLoading ? (
            <div className="px-3 py-2.5 text-sm text-slate-400 dark:text-slate-500" data-testid="mention-loading">
              Loading suggestions…
            </div>
          ) : filtered.map((item, i) => {
            const active = i === selectedIndex;
            const baseClass = `flex items-center gap-2 w-full px-3 py-2 text-left transition-colors ${
              active ? 'bg-slate-100 dark:bg-[#3a3d41]' : 'hover:bg-slate-50 dark:hover:bg-white/5'
        }`;
        if (item.kind === 'group') {
          return (
            <button
              key={`group-${item.id}`}
              type="button"
              className={baseClass}
              onMouseDown={(e) => { e.preventDefault(); onSelect(item); }}
              onMouseEnter={() => setSelectedIndex(i)}
              data-testid={`mention-option-group-${item.id}`}
            >
              <span
                className="inline-flex items-center justify-center w-6 h-6 rounded-md text-[10px] font-semibold text-white shrink-0"
                style={{ backgroundColor: item.color || '#6366f1' }}
              >
                <Users className="w-3.5 h-3.5" />
              </span>
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">@{item.tag}</span>
              <span className="text-xs text-slate-400 dark:text-slate-500 truncate">{item.name}</span>
              <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-auto shrink-0">
                {item.member_count} {item.member_count === 1 ? 'member' : 'members'}
              </span>
            </button>
          );
        }
        return (
          <button
            key={`user-${item.user_id}`}
            type="button"
            className={baseClass}
            onMouseDown={(e) => { e.preventDefault(); onSelect(item); }}
            onMouseEnter={() => setSelectedIndex(i)}
            data-testid={`mention-option-${item.user_id}`}
          >
            <UserAvatar userId={item.user_id} name={item.user_name} size="xs" />
            <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{item.user_name}</span>
            <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-auto">{item.role}</span>
          </button>
        );
      })}
    </div>,
        portalTarget
      )}
    </>
  );
});

MentionSuggestions.displayName = 'MentionSuggestions';
export default MentionSuggestions;
